import axios from 'axios';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const client = axios.create({ baseURL: BASE });

// Attach JWT to every request
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('wgi_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 → clear token, bounce to login
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('wgi_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  },
);

// ---- snake_case <-> camelCase helpers ----
const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date);

export const snakeToCamel = (obj) => {
  if (Array.isArray(obj)) return obj.map(snakeToCamel);
  if (!isPlainObject(obj)) return obj;
  const out = {};
  for (const k of Object.keys(obj)) {
    const ck = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    out[ck] = snakeToCamel(obj[k]);
  }
  return out;
};

export const camelToSnake = (obj) => {
  if (Array.isArray(obj)) return obj.map(camelToSnake);
  if (!isPlainObject(obj)) return obj;
  const out = {};
  for (const k of Object.keys(obj)) {
    const sk = k.replace(/[A-Z]/g, (m) => '_' + m.toLowerCase());
    out[sk] = camelToSnake(obj[k]);
  }
  return out;
};

const extract = (e) => e.response?.data?.detail || e.message || 'Request failed';

const run = async (promise) => {
  try {
    const r = await promise;
    return snakeToCamel(r.data);
  } catch (e) {
    const err = new Error(extract(e));
    err.status = e.response?.status;
    throw err;
  }
};

export const api = {
  // Auth
  login: (email, password) => run(client.post('/auth/login', { email, password })),
  me: () => run(client.get('/auth/me')),
  changePassword: (currentPassword, newPassword) =>
    run(client.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword })),

  // Items
  listItems: () => run(client.get('/items')),
  createItem: (data) => run(client.post('/items', camelToSnake(data))),
  updateItem: (code, data) => run(client.patch(`/items/${code}`, camelToSnake(data))),
  deleteItem: (code) => run(client.delete(`/items/${code}`)),

  // Kits
  listKits: () => run(client.get('/kits')),
  createKit: (data) => run(client.post('/kits', camelToSnake(data))),
  updateKit: (code, data) => run(client.patch(`/kits/${code}`, camelToSnake(data))),
  deleteKit: (code) => run(client.delete(`/kits/${code}`)),

  // Projects
  listProjects: () => run(client.get('/projects')),
  getProject: (id) => run(client.get(`/projects/${id}`)),
  createProject: (data) => run(client.post('/projects', camelToSnake(data))),
  updateProject: (id, data) => run(client.patch(`/projects/${id}`, camelToSnake(data))),
  deleteProject: (id) => run(client.delete(`/projects/${id}`)),
  completeProject: (id) => run(client.post(`/projects/${id}/complete`)),
  reactivateProject: (id) => run(client.post(`/projects/${id}/reactivate`)),
  unlinkProjectFromPm: (id) => run(client.post(`/projects/${id}/unlink-from-pm`)),

  addItemToProject: (id, code) => run(client.post(`/projects/${id}/gear/items`, { code })),
  removeItemFromProject: (id, code) => run(client.delete(`/projects/${id}/gear/items/${code}`)),
  addKitToProject: (id, code) => run(client.post(`/projects/${id}/gear/kits`, { code })),
  removeKitFromProject: (id, code) => run(client.delete(`/projects/${id}/gear/kits/${code}`)),
  clearProjectGear: (id) => run(client.delete(`/projects/${id}/gear`)),
  setGearDaySpan: (id, code, body) =>
    run(client.patch(`/projects/${id}/gear/${code}/day-span`, camelToSnake(body))),

  scanProject: (id, code, direction) =>
    run(client.post(`/projects/${id}/scan`, { code, direction })),
  returnAllPacked: (id) => run(client.post(`/projects/${id}/return-all`)),

  // Transactions
  listTransactions: (params = {}) => run(client.get('/transactions', { params })),

  // Categories
  listCategories: () => run(client.get('/categories')),
  createCategory: (data) => run(client.post('/categories', camelToSnake(data))),
  updateCategory: (id, data) => run(client.patch(`/categories/${id}`, camelToSnake(data))),
  reorderCategories: (ids) => run(client.post('/categories/reorder', { ids })),
  deleteCategory: (id) => run(client.delete(`/categories/${id}`)),

  // Media
  listMedia: () => run(client.get('/media')),
  setMediaStatus: (code, status) =>
    run(client.patch(`/items/${code}/media-status`, { status })),

  // Project Manager sync
  pmListProjects: () => run(client.get('/sync/pm/projects')),
  pmHealth: () => run(client.get('/sync/pm/health')),
  pmWebhookStatus: () => run(client.get('/sync/pm/webhook-status')),
  pmWebhookEvents: (limit = 25) =>
    run(client.get(`/sync/pm/webhook-events?limit=${limit}`)),
  pmWebhookTest: (webhookUrl) =>
    run(client.post('/sync/pm/webhook-test', { webhook_url: webhookUrl || '' })),
  pmImportProject: (externalId) => run(client.post(`/sync/pm/import/${externalId}`)),
  refreshProjectFromPm: (id) => run(client.post(`/projects/${id}/refresh-from-pm`)),
  listProjectDays: (id) => run(client.get(`/projects/${id}/days`)),
};
